/**
 * Wallet Service - Direct calls to Go Wallet Service
 *
 * Flow:
 * 1. Get JWT token from Laravel (/api/v1/wallet/token)
 * 2. Call Go wallet service directly with JWT
 */

import axios from 'axios';
import { MAX_FILE_SIZE } from '@/services/walletConstants';

// Retry configuration
const RETRY_CONFIG = {
  maxAttempts: 3,
  initialDelayMs: 500,
  maxDelayMs: 5000,
  multiplier: 2.0,
  jitter: 0.2,
};

// Max response size to prevent memory exhaustion (10MB)
const MAX_RESPONSE_SIZE = 10 * 1024 * 1024;

/**
 * Validate a path parameter before URL interpolation.
 * Prevents path traversal attacks (/, .., \).
 * @param {*} value - The parameter value
 * @param {'integer'|'string'} type - Expected type ('integer' for IDs, 'string' for codes)
 */
function validatePathParam(value, type = 'integer') {
  if (value === null || value === undefined || value === '') {
    throw new Error('Path parameter is required');
  }
  const str = String(value);
  if (str.includes('/') || str.includes('..') || str.includes('\\')) {
    throw new Error('Invalid path parameter: contains illegal characters');
  }
  if (type === 'integer') {
    if (!/^\d+$/.test(str)) {
      throw new Error('Path parameter must be a positive integer');
    }
  } else if (type === 'string') {
    if (!/^[a-zA-Z0-9_-]+$/.test(str)) {
      throw new Error('Path parameter must be alphanumeric');
    }
  }
  return str;
}

/**
 * Extract error info from axios or fetch errors.
 * Returns { message, requestId } for error notification display.
 * @param {Error} error - Axios error or fetch-wrapped error
 * @returns {{ message: string, requestId: string|null }}
 */
function extractErrorInfo(error) {
  let message = 'ເກີດຂໍ້ຜິດພາດ';
  let requestId = null;

  // Check for rate limit
  if (error.isRateLimited) {
    return { message: 'ກະລຸນາລໍຖ້າແລ້ວລອງໃໝ່', requestId: error.requestId || null };
  }

  let messageKey = null;

  // Axios error with response data
  if (error.response?.data) {
    const data = error.response.data;
    requestId = data.request_id || null;
    message = data.error?.message || data.message || message;
    messageKey = data.error?.message_key || data.message_key || null;
  }

  // Fetch-wrapped error (set by fetchWithAuth)
  if (error.requestId) {
    requestId = error.requestId;
  }
  if (error.serverMessage) {
    message = error.serverMessage;
  }

  // Fallback for network errors — show user-friendly Lao message instead of raw "Network Error"
  if (!error.response && !error.serverMessage) {
    const isNetworkError = error.code === 'ERR_NETWORK' ||
      error.code === 'ECONNABORTED' ||
      error.code === 'ECONNREFUSED' ||
      error.code === 'ECONNRESET' ||
      error.code === 'ETIMEDOUT' ||
      error.message?.includes('Network Error') ||
      error.message?.includes('timeout');
    if (isNetworkError) {
      message = 'ບໍ່ສາມາດເຊື່ອມຕໍ່ລະບົບກະເປົ໋າໄດ້';
    } else if (error.message) {
      message = error.message;
    }
  }

  return { message, requestId, messageKey };
}

/**
 * Check if error is retryable (network errors, 5xx, 429)
 */
function isRetryableError(error) {
  // Network errors (no response)
  if (!error.response) {
    return error.code === 'ECONNABORTED' || // Timeout
           error.code === 'ECONNREFUSED' ||
           error.code === 'ECONNRESET' ||
           error.code === 'ETIMEDOUT' ||
           error.code === 'ERR_NETWORK' ||
           error.message?.includes('Network Error');
  }
  // Server errors and rate limiting
  const status = error.response?.status;
  return status === 429 || (status >= 500 && status <= 599);
}

/**
 * Calculate retry delay with exponential backoff and jitter
 */
function calculateRetryDelay(attempt) {
  let delay = RETRY_CONFIG.initialDelayMs * Math.pow(RETRY_CONFIG.multiplier, attempt - 1);
  delay = Math.min(delay, RETRY_CONFIG.maxDelayMs);
  // Add jitter: ±20%
  const jitterRange = delay * RETRY_CONFIG.jitter;
  delay = delay - jitterRange + (Math.random() * 2 * jitterRange);
  return Math.max(0, Math.floor(delay));
}

/**
 * Sleep helper
 */
function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

class WalletService {
  constructor() {
    this.token = null;
    this.walletServiceUrl = null;
    this.tokenExpiry = null;
    this.tokenRefreshPromise = null;
    this.laravelAxiosRef = null;
    this.pendingRequests = 0; // Track concurrent requests
    this.maxConcurrentRequests = 10; // Limit concurrent requests
    this._waitQueue = []; // Promise-based semaphore queue
  }

  /**
   * Get JWT token from Laravel for Go wallet service access
   * Uses lock to prevent race conditions when multiple requests happen simultaneously
   */
  async getToken(laravelAxios) {
    // Store reference for potential re-auth
    this.laravelAxiosRef = laravelAxios;

    // Check if token is still valid (with 5 min buffer)
    if (this.token && this.tokenExpiry && Date.now() < this.tokenExpiry - 300000) {
      return this.token;
    }

    // If already refreshing, wait for that promise (prevents race condition)
    if (this.tokenRefreshPromise) {
      return this.tokenRefreshPromise;
    }

    // Start token refresh
    this.tokenRefreshPromise = this._refreshToken(laravelAxios);
    try {
      return await this.tokenRefreshPromise;
    } finally {
      this.tokenRefreshPromise = null;
    }
  }

  /**
   * Internal method to refresh token
   */
  async _refreshToken(laravelAxios) {
    try {
      const response = await laravelAxios.get('wallet/token');
      this.token = response.data.token;
      this.walletServiceUrl = response.data.wallet_service_url;
      this.tokenExpiry = Date.now() + (response.data.expires_in * 1000);
      return this.token;
    } catch (error) {
      console.error('Failed to get wallet token:', error);
      this.clearToken();
      throw error;
    }
  }

  /**
   * Clear token (used on 401 or logout)
   */
  clearToken() {
    this.token = null;
    this.tokenExpiry = null;
    this.tokenRefreshPromise = null;
  }

  /**
   * Create axios instance for Go wallet service with robust error handling
   * Features:
   * - 30s timeout
   * - 401 token refresh
   * - Retry on network errors with exponential backoff
   * - Response size validation
   * - Concurrent request limiting
   * - Optional AbortController signal support
   * @param {Object} [options] - Optional configuration
   * @param {AbortSignal} [options.signal] - AbortController signal for request cancellation
   */
  getWalletAxios(options = {}) {
    if (!this.token || !this.walletServiceUrl) {
      throw new Error('Wallet token not initialized. Call getToken() first.');
    }

    const config = {
      baseURL: this.walletServiceUrl,
      timeout: 30000, // 30 second timeout for wallet operations
      headers: {
        'Authorization': `Bearer ${this.token}`,
        'Content-Type': 'application/json',
      },
      maxContentLength: MAX_RESPONSE_SIZE,
      maxBodyLength: MAX_RESPONSE_SIZE,
    };

    if (options.signal) {
      config.signal = options.signal;
    }

    const instance = axios.create(config);

    // Request interceptor for concurrent request limiting (promise-based semaphore)
    instance.interceptors.request.use(
      async config => {
        if (this.pendingRequests >= this.maxConcurrentRequests) {
          await new Promise(resolve => this._waitQueue.push(resolve));
        }
        this.pendingRequests++;
        return config;
      },
      error => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling and retry
    instance.interceptors.response.use(
      response => {
        this._releaseSlot();
        return response;
      },
      async error => {
        this._releaseSlot();

        // Handle 401 - token expired
        if (error.response?.status === 401 && this.laravelAxiosRef) {
          this.clearToken();
        }
        return Promise.reject(error);
      }
    );

    return instance;
  }

  _releaseSlot() {
    this.pendingRequests--;
    if (this._waitQueue.length > 0) {
      const next = this._waitQueue.shift();
      next();
    }
  }

  /**
   * Fetch with authentication, timeout, and error handling.
   * Wraps native fetch() for file download endpoints.
   * @param {string} url - Full URL to fetch
   * @param {Object} [options] - Fetch options
   * @param {number} [options.timeout=120000] - Timeout in ms (default 120s for downloads)
   * @param {AbortSignal} [options.signal] - External abort signal
   * @returns {Promise<Response>} Fetch response (caller handles blob)
   */
  async fetchWithAuth(url, options = {}) {
    if (!this.token) {
      throw new Error('Wallet token not initialized. Call getToken() first.');
    }

    const timeout = options.timeout || 120000;
    const controller = new AbortController();
    let timeoutId;

    // Link external signal if provided
    if (options.signal) {
      options.signal.addEventListener('abort', () => controller.abort());
    }

    timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        headers: {
          'Authorization': `Bearer ${this.token}`,
          ...(options.headers || {}),
        },
      });

      clearTimeout(timeoutId);

      if (response.status === 401) {
        this.clearToken();
      }

      if (!response.ok) {
        // Try to parse error body for request_id and message
        let errorBody = {};
        try {
          errorBody = await response.clone().json();
        } catch { /* not JSON */ }

        const err = new Error(errorBody.error?.message || errorBody.message || `Request failed with status ${response.status}`);
        err.status = response.status;
        err.requestId = errorBody.request_id || null;
        err.serverMessage = errorBody.error?.message || errorBody.message || null;
        throw err;
      }

      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError' && !options.signal?.aborted) {
        const timeoutErr = new Error('Request timed out');
        timeoutErr.code = 'ECONNABORTED';
        throw timeoutErr;
      }
      throw error;
    }
  }

  /**
   * Execute API call with automatic retry on network errors
   * @param {Function} apiCall - Async function that makes the API call
   * @param {number} maxRetries - Maximum retry attempts (default: 3)
   */
  async withNetworkRetry(apiCall, maxRetries = RETRY_CONFIG.maxAttempts) {
    let lastError;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await apiCall();
      } catch (error) {
        lastError = error;

        // Don't retry if not retryable or last attempt
        if (!isRetryableError(error) || attempt === maxRetries) {
          // Mark 429 for user-facing message
          if (error.response?.status === 429) {
            error.isRateLimited = true;
            error.requestId = error.response?.data?.request_id || null;
          }
          throw error;
        }

        // Calculate delay and wait
        const delay = calculateRetryDelay(attempt);
        console.warn(`Wallet API retry attempt ${attempt}/${maxRetries} after ${delay}ms`, {
          error: error.message,
          code: error.code,
        });
        await sleep(delay);
      }
    }

    throw lastError;
  }

  /**
   * Execute API call with automatic retry on 401 and network errors
   * Combines token refresh retry with network error retry
   */
  async withRetry(laravelAxios, apiCall, maxRetries = 1) {
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        await this.getToken(laravelAxios);
        // Wrap in network retry for transient failures
        return await this.withNetworkRetry(apiCall);
      } catch (error) {
        const is401 = error.response?.status === 401;
        const isLastAttempt = attempt === maxRetries;

        if (is401 && !isLastAttempt) {
          // Clear token and retry
          this.clearToken();
          continue;
        }
        throw error;
      }
    }
  }

  /**
   * List all bank providers
   */
  async listBanks(laravelAxios) {
    return this.withRetry(laravelAxios, async () => {
      const walletAxios = this.getWalletAxios();
      const response = await walletAxios.get('/api/v1/admin/banks');
      return response.data;
    });
  }

  /**
   * Get the treasury "mirror bank balance" snapshot (admin, direct JWT to Go).
   * Returns the standard envelope; the treasury object is in `.data`.
   */
  async getTreasury(laravelAxios) {
    return this.withRetry(laravelAxios, async () => {
      const walletAxios = this.getWalletAxios();
      const response = await walletAxios.get('/api/v1/admin/wallets/treasury');
      return response.data;
    });
  }

  /**
   * Activate a bank provider
   */
  async activateBank(laravelAxios, bankCode) {
    validatePathParam(bankCode, 'string');
    return this.withRetry(laravelAxios, async () => {
      const walletAxios = this.getWalletAxios();
      const response = await walletAxios.put(`/api/v1/admin/banks/${bankCode}/activate`, null, {
        headers: { 'Idempotency-Key': `bank-activate-${crypto.randomUUID()}` },
      });
      return response.data;
    });
  }

  /**
   * Get wallet balances for a branch (via admin route with JWT)
   */
  async getWallets(laravelAxios, branchId) {
    validatePathParam(branchId, 'integer');
    return this.withRetry(laravelAxios, async () => {
      const walletAxios = this.getWalletAxios();
      const response = await walletAxios.get(`/api/v1/admin/wallets/${branchId}`);
      return response.data;
    });
  }

  /**
   * Get transaction history for a branch (via admin route with JWT)
   */
  async getTransactions(laravelAxios, branchId, params = {}, signal) {
    validatePathParam(branchId, 'integer');
    return this.withRetry(laravelAxios, async () => {
      const walletAxios = this.getWalletAxios({ signal });
      const response = await walletAxios.get(`/api/v1/admin/wallets/${branchId}/transactions`, { params });
      return response.data;
    });
  }

  /**
   * Get all transactions across all branches (admin only)
   */
  async getAllTransactions(laravelAxios, params = {}, signal) {
    return this.withRetry(laravelAxios, async () => {
      const walletAxios = this.getWalletAxios({ signal });
      const response = await walletAxios.get('/api/v1/admin/wallets/transactions', { params });
      return response.data;
    });
  }

  /**
   * Export all transactions to Excel (legacy - sync export for small datasets)
   */
  async exportAllTransactions(laravelAxios, params = {}) {
    await this.getToken(laravelAxios);

    const queryString = new URLSearchParams(params).toString();
    const url = `${this.walletServiceUrl}/api/v1/admin/wallets/transactions/export?${queryString}`;

    const response = await this.fetchWithAuth(url);

    // Download the file
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `transactions_${new Date().toISOString().split('T')[0]}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }

  /**
   * Create an async export job (for large datasets)
   * @param {Object} laravelAxios - Laravel axios instance
   * @param {string} jobType - 'TRANSACTIONS' or 'WALLETS'
   * @param {Object} filters - Filter parameters
   */
  async createExportJob(laravelAxios, jobType, filters = {}) {
    return this.withRetry(laravelAxios, async () => {
      const walletAxios = this.getWalletAxios();
      const response = await walletAxios.post('/api/v1/admin/export', {
        job_type: jobType,
        filters: filters,
      }, {
        headers: { 'Idempotency-Key': `export-${crypto.randomUUID()}` },
      });
      return response.data;
    });
  }

  /**
   * Get export job status
   * @param {Object} laravelAxios - Laravel axios instance
   * @param {number} jobId - Export job ID
   */
  async getExportJob(laravelAxios, jobId) {
    validatePathParam(jobId, 'integer');
    return this.withRetry(laravelAxios, async () => {
      const walletAxios = this.getWalletAxios();
      const response = await walletAxios.get(`/api/v1/admin/export/${jobId}`);
      return response.data;
    });
  }

  /**
   * List user's export jobs
   * @param {Object} laravelAxios - Laravel axios instance
   * @param {number} limit - Maximum jobs to return
   */
  async listExportJobs(laravelAxios, limit = 20) {
    return this.withRetry(laravelAxios, async () => {
      const walletAxios = this.getWalletAxios();
      const response = await walletAxios.get('/api/v1/admin/export', { params: { limit } });
      return response.data;
    });
  }

  /**
   * Download completed export file
   * @param {Object} laravelAxios - Laravel axios instance
   * @param {number} jobId - Export job ID
   */
  async downloadExport(laravelAxios, jobId) {
    validatePathParam(jobId, 'integer');
    await this.getToken(laravelAxios);

    const url = `${this.walletServiceUrl}/api/v1/admin/export/${jobId}/download`;

    const response = await this.fetchWithAuth(url);

    // Download the file
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;

    // Get filename from Content-Disposition header or use default
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `ລາຍການເຄື່ອນໄຫວ_${jobId}.xlsx`;
    if (contentDisposition) {
      // Prefer filename* (RFC 5987, supports Lao/Unicode)
      const utf8Match = contentDisposition.match(/filename\*=UTF-8''(.+?)(?:;|$)/);
      if (utf8Match) {
        filename = decodeURIComponent(utf8Match[1].trim());
      } else {
        // Fallback to filename
        const match = contentDisposition.match(/filename="?([^";]+)"?/);
        if (match) {
          filename = match[1].trim();
        }
      }
    }

    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }

  /**
   * Manual top-up (calls Go directly with multipart form)
   * No retry — financial write (avoid double-charge).
   * Sends idempotency_key for dedup safety.
   * @param {Object} laravelAxios - Laravel axios instance (for getting JWT token)
   * @param {number} branchId - Branch ID
   * @param {string} walletType - 'FREIGHT' or 'COD'
   * @param {number} amount - Amount to top up
   * @param {string} reason - Reason for top-up
   * @param {File} slipImage - Slip image file
   */
  async manualTopUp(laravelAxios, branchId, walletType, amount, reason, slipImage, idempotencyKey) {
    validatePathParam(branchId, 'integer');
    // Validate file size client-side (5MB max)
    if (slipImage && slipImage.size > MAX_FILE_SIZE) {
      throw new Error('Slip image size exceeds 5MB limit');
    }

    await this.getToken(laravelAxios);

    const formData = new FormData();
    formData.append('branch_id', branchId);
    formData.append('wallet_type', walletType);
    formData.append('amount', amount);
    formData.append('reason', reason);
    formData.append('slip_image', slipImage);

    // [B1] Prefer the caller-supplied STABLE per-intent key (TopUp.vue generates one per dialog/intent
    // and reuses it across resubmits) so a human retry after a 60s upload timeout is deduped by Go →
    // exactly-once credit. Fall back to a fresh key only for legacy callers that pass none.
    const key = idempotencyKey || `topup-${crypto.randomUUID()}`;
    formData.append('idempotency_key', key);

    // Use longer timeout for file upload (60s)
    const response = await axios.post(
      `${this.walletServiceUrl}/api/v1/admin/topup/manual`,
      formData,
      {
        timeout: 60000, // 60s for file upload
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'multipart/form-data',
          'Idempotency-Key': key,
        },
        maxContentLength: MAX_RESPONSE_SIZE,
        maxBodyLength: MAX_FILE_SIZE,
      }
    );
    return response.data;
  }

  /**
   * Transfer money between a branch's wallet types (FREIGHT <-> COD) — Accounting Manager only.
   * Calls Go directly with a multipart form. No retry — financial write (avoid double-move).
   * Sends idempotency_key (body + header) for dedup safety.
   *
   * Seam contract (Go internal/wallet/dto.go TransferReq): fields branch_id, source_wallet_type,
   * dest_wallet_type, amount, remark (required, <=500), slip_image (file), idempotency_key
   * (required, 16-64 chars — `transfer-<uuid>` = 45, in range). Endpoint POST
   * /api/v1/admin/wallets/transfer. Server gates on the `can_transfer` JWT claim (the FE button is
   * cosmetic) and a config flag (when OFF Go returns a clean 404, not 500).
   * @param {Object} laravelAxios - Laravel axios instance (for getting JWT token)
   * @param {number} branchId - Branch ID
   * @param {string} sourceWalletType - 'FREIGHT' or 'COD'
   * @param {string} destWalletType - 'FREIGHT' or 'COD' (must differ from source)
   * @param {number} amount - Amount to transfer
   * @param {string} remark - Required reason (1-500 chars)
   * @param {File} slipImage - Slip image file
   * @param {string} idempotencyKey - Stable per-intent key (Transfer.vue mints one per intent)
   */
  async transferMoney(laravelAxios, branchId, sourceWalletType, destWalletType, amount, remark, slipImage, idempotencyKey) {
    validatePathParam(branchId, 'integer');
    // Validate file size client-side (5MB max) — mirrors manualTopUp.
    if (slipImage && slipImage.size > MAX_FILE_SIZE) {
      throw new Error('Slip image size exceeds 5MB limit');
    }

    await this.getToken(laravelAxios);

    const formData = new FormData();
    formData.append('branch_id', branchId);
    formData.append('source_wallet_type', sourceWalletType);
    formData.append('dest_wallet_type', destWalletType);
    formData.append('amount', amount);
    formData.append('remark', remark);
    formData.append('slip_image', slipImage);

    // Stable per-intent key (Transfer.vue generates one per dialog/intent and reuses it across
    // resubmits) so a human retry after a 60s upload timeout is deduped by Go -> exactly-once move.
    // Fall back to a fresh 16-64 char key only for callers that pass none.
    const key = idempotencyKey || `transfer-${crypto.randomUUID()}`;
    formData.append('idempotency_key', key);

    // Use longer timeout for file upload (60s).
    // IMPORTANT: send the key in the BODY only — deliberately NO 'Idempotency-Key' header. Go's
    // transfer handler reads the body idempotency_key as the money-idempotency source of truth
    // (the claim table serializes it → exactly-once). If the header IS present, the admin
    // idempotency middleware reads up to MaxRequestBodySize (1MB) of the body to hash the request,
    // which 413s a >1MB (≤5MB) photo slip. (manualTopUp sends the header and is latently affected by
    // the same cap on big slips; body-only is the correct contract for the transfer endpoint.)
    const response = await axios.post(
      `${this.walletServiceUrl}/api/v1/admin/wallets/transfer`,
      formData,
      {
        timeout: 60000, // 60s for file upload
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'multipart/form-data',
        },
        maxContentLength: MAX_RESPONSE_SIZE,
        maxBodyLength: MAX_FILE_SIZE,
      }
    );
    return response.data;
  }

  /**
   * Export branch transactions to Excel (with JWT auth)
   * @param {Object} laravelAxios - Laravel axios instance (for getting JWT token)
   * @param {number} branchId - Branch ID
   * @param {Object} params - Filter parameters
   */
  async exportBranchTransactions(laravelAxios, branchId, params = {}) {
    validatePathParam(branchId, 'integer');
    await this.getToken(laravelAxios);

    const filterParams = { ...params, branch_id: branchId };
    const queryString = new URLSearchParams(filterParams).toString();
    const url = `${this.walletServiceUrl}/api/v1/admin/wallets/transactions/export?${queryString}`;

    const response = await this.fetchWithAuth(url);

    // Download the file
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;

    // Get filename from Content-Disposition header or use default
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `transactions_${branchId}_${new Date().toISOString().split('T')[0]}.xlsx`;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename=([^;]+)/);
      if (match) {
        filename = match[1].trim();
      }
    }

    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }

  /**
   * Update wallet limits (calls Go directly)
   * @param {Object} laravelAxios - Laravel axios instance (for getting JWT token)
   * @param {number} walletId - Wallet ID
   * @param {number} minimumBalance - Minimum balance for warning
   * @param {number} minimumNegativeLimit - Maximum negative limit (debt limit)
   */
  async updateLimits(laravelAxios, walletId, minimumBalance, minimumNegativeLimit) {
    validatePathParam(walletId, 'integer');
    return this.withRetry(laravelAxios, async () => {
      const walletAxios = this.getWalletAxios();
      const response = await walletAxios.put(`/api/v1/admin/wallets/${walletId}/limits`, {
        minimum_balance: minimumBalance,
        minimum_negative_limit: minimumNegativeLimit,
      }, {
        headers: { 'Idempotency-Key': `limits-${crypto.randomUUID()}` },
      });
      return response.data;
    });
  }

  /**
   * Enable or disable wallet for a branch (calls Laravel - wallet_enabled is managed by HAL)
   * @param {Object} laravelAxios - Laravel axios instance
   * @param {number} branchId - Branch ID
   * @param {boolean} enabled - Whether to enable or disable wallet
   */
  async setWalletEnabled(laravelAxios, branchId, enabled) {
    // Uses dedicated wallet endpoint (SuperAdmin|accountingmanager roles)
    const response = await laravelAxios.patch(`wallet/branches/${branchId}`, {
      wallet_enabled: enabled,
    });
    return response.data;
  }

  /**
   * Batch enable or disable wallets for multiple branches (single request to Laravel)
   * @param {Object} laravelAxios - Laravel axios instance
   * @param {number[]} branchIds - Array of branch IDs
   * @param {boolean} enabled - Whether to enable or disable
   * @returns {{ success: boolean, message: string, succeeded: number[], failed: Array<{branch_id: number, error: string}> }}
   */
  async batchSetWalletEnabled(laravelAxios, branchIds, enabled) {
    const response = await laravelAxios.post('wallet/branches/batch-update', {
      branch_ids: branchIds,
      wallet_enabled: enabled,
    });
    return response.data;
  }
}

// Singleton instance
const walletServiceInstance = new WalletService();
export default walletServiceInstance;

// Export utilities for use in Vue components
export { extractErrorInfo, validatePathParam };

/**
 * Fetch branches for filter dropdowns / label maps (eager, full page).
 * Uses the lightweight branch-options endpoint (NO Go balance fan-out) — these callers only need
 * id/name/prefix, never balances; hitting wallet/branches would issue one Go HMAC call per page-branch.
 * Includes wallet-DISABLED branches so a disabled branch stays selectable to inspect its history.
 * @param {Object} laravelAxios - Laravel axios instance
 * @returns {Promise<Array>} Array of branch objects
 */
export async function fetchWalletBranches(laravelAxios) {
  return fetchBranchOptions(laravelAxios, { perPage: 100 });
}

/**
 * Lightweight server-side branch search for the BranchSelect typeahead (no Go calls).
 * Includes wallet-disabled branches. Pass `id` to resolve a single branch (preselect/deep-link).
 * @param {Object} laravelAxios - Laravel axios instance
 * @param {Object} [opts]
 * @param {string} [opts.search] - name/prefix LIKE filter
 * @param {number} [opts.perPage=50] - page size (server caps at 200)
 * @param {number|string|null} [opts.id] - resolve exactly one branch by id
 * @returns {Promise<Array>} Array of branch options ({id, name, prefix, type, wallet_enabled})
 */
export async function fetchBranchOptions(laravelAxios, { search = '', perPage = 50, id = null } = {}) {
  const params = { per_page: perPage };
  if (search) params.search = search;
  if (id != null && id !== '') params.id = id;
  const response = await laravelAxios.get('wallet/branch-options', { params });
  return response.data.data || [];
}

// --- Slip image ---

/**
 * Get transaction slip as a blob URL (JWT-authenticated).
 * Returns null if no slip is attached (404).
 * Caller must revoke the URL via URL.revokeObjectURL() when done.
 * @param {Object} laravelAxios - Laravel axios instance (for getting JWT token)
 * @param {number} transactionId - Transaction ID
 * @returns {Promise<{url: string, contentType: string}|null>} Object URL + content type, or null if no slip
 */
WalletService.prototype.getTransactionSlip = async function(laravelAxios, transactionId) {
  validatePathParam(transactionId, 'integer');
  await this.getToken(laravelAxios);

  const url = `${this.walletServiceUrl}/api/v1/admin/wallets/transactions/${transactionId}/slip`;

  try {
    const response = await this.fetchWithAuth(url, { timeout: 30000 });
    const contentType = response.headers.get('Content-Type') || '';
    const blob = await response.blob();
    return { url: URL.createObjectURL(blob), contentType };
  } catch (error) {
    // 404 = no slip attached (expected for QR top-ups)
    if (error.status === 404) {
      return null;
    }
    throw error;
  }
};

// --- Additional methods ---

WalletService.prototype.listAuditLogs = async function(laravelAxios, params = {}, signal) {
  return this.withRetry(laravelAxios, async () => {
    const walletAxios = this.getWalletAxios({ signal });
    const response = await walletAxios.get('/api/v1/admin/audit-logs', { params });
    return response.data;
  });
};

WalletService.prototype.getAuditLogOptions = async function(laravelAxios) {
  return this.withRetry(laravelAxios, async () => {
    const walletAxios = this.getWalletAxios();
    const response = await walletAxios.get('/api/v1/admin/audit-logs/options');
    return response.data;
  });
};

WalletService.prototype.updateBankConfig = async function(laravelAxios, bankCode, config) {
  validatePathParam(bankCode, 'string');
  return this.withRetry(laravelAxios, async () => {
    const walletAxios = this.getWalletAxios();
    const response = await walletAxios.put(`/api/v1/admin/banks/${bankCode}/config`, config, {
      headers: { 'Idempotency-Key': `bank-config-${crypto.randomUUID()}` },
    });
    return response.data;
  });
};

// --- Pending top-ups ---

WalletService.prototype.listPendingTopups = async function(laravelAxios, params = {}, signal) {
  return this.withRetry(laravelAxios, async () => {
    const walletAxios = this.getWalletAxios({ signal });
    const response = await walletAxios.get('/api/v1/admin/topup/pending', { params });
    return response.data;
  });
};

WalletService.prototype.resolveTopup = async function(laravelAxios, id, reason, slipFile, idempotencyKey) {
  validatePathParam(id, 'integer');
  if (slipFile && slipFile.size > MAX_FILE_SIZE) {
    throw new Error('Slip image size exceeds 5MB limit');
  }

  await this.getToken(laravelAxios);

  // [B1] Stable per-intent key (caller supplies one per resolve-intent, reused across resubmits) so a
  // retried resolve is deduped by Go. Fall back to a fresh key for legacy callers.
  const key = idempotencyKey || `resolve-topup-${crypto.randomUUID()}`;

  const formData = new FormData();
  formData.append('reason', reason);
  formData.append('slip_image', slipFile);
  // [B1] Send the key in the BODY too (not header-only): Go's service-layer dedup reads the body
  // field, so this gives the resolve path the same dedup parity as manual top-up.
  formData.append('idempotency_key', key);

  const response = await axios.post(
    `${this.walletServiceUrl}/api/v1/admin/topup/${id}/resolve`,
    formData,
    {
      timeout: 60000,
      headers: {
        'Authorization': `Bearer ${this.token}`,
        'Content-Type': 'multipart/form-data',
        'Idempotency-Key': key,
      },
      maxContentLength: MAX_RESPONSE_SIZE,
      maxBodyLength: MAX_FILE_SIZE,
    }
  );
  return response.data;
};

// --- Job queue ---

WalletService.prototype.getJobStats = async function(laravelAxios) {
  return this.withRetry(laravelAxios, async () => {
    const walletAxios = this.getWalletAxios();
    const response = await walletAxios.get('/api/v1/admin/jobs/stats');
    return response.data;
  });
};

WalletService.prototype.listJobs = async function(laravelAxios, params = {}, signal) {
  return this.withRetry(laravelAxios, async () => {
    const walletAxios = this.getWalletAxios({ signal });
    const response = await walletAxios.get('/api/v1/admin/jobs', { params });
    return response.data;
  });
};

WalletService.prototype.getJob = async function(laravelAxios, jobId) {
  validatePathParam(jobId, 'string');
  return this.withRetry(laravelAxios, async () => {
    const walletAxios = this.getWalletAxios();
    const response = await walletAxios.get(`/api/v1/admin/jobs/${jobId}`);
    return response.data;
  });
};

WalletService.prototype.cancelJob = async function(laravelAxios, jobId) {
  validatePathParam(jobId, 'string');
  return this.withRetry(laravelAxios, async () => {
    const walletAxios = this.getWalletAxios();
    const response = await walletAxios.delete(`/api/v1/admin/jobs/${jobId}`, {
      headers: { 'Idempotency-Key': `job-cancel-${crypto.randomUUID()}` },
    });
    return response.data;
  });
};

// ==================== Holiday Management ====================

WalletService.prototype.listHolidays = async function(laravelAxios, params = {}) {
  return this.withRetry(laravelAxios, async () => {
    const walletAxios = this.getWalletAxios();
    const response = await walletAxios.get('/api/v1/admin/holidays', { params });
    return response.data;
  });
};

WalletService.prototype.createHoliday = async function(laravelAxios, data) {
  return this.withRetry(laravelAxios, async () => {
    const walletAxios = this.getWalletAxios();
    const response = await walletAxios.post('/api/v1/admin/holidays', data, {
      headers: { 'Idempotency-Key': `holiday-create-${crypto.randomUUID()}` },
    });
    return response.data;
  });
};

WalletService.prototype.updateHoliday = async function(laravelAxios, id, data) {
  validatePathParam(id, 'integer');
  return this.withRetry(laravelAxios, async () => {
    const walletAxios = this.getWalletAxios();
    const response = await walletAxios.put(`/api/v1/admin/holidays/${id}`, data, {
      headers: { 'Idempotency-Key': `holiday-update-${crypto.randomUUID()}` },
    });
    return response.data;
  });
};

WalletService.prototype.deleteHoliday = async function(laravelAxios, id) {
  validatePathParam(id, 'integer');
  return this.withRetry(laravelAxios, async () => {
    const walletAxios = this.getWalletAxios();
    const response = await walletAxios.delete(`/api/v1/admin/holidays/${id}`, {
      headers: { 'Idempotency-Key': `holiday-delete-${crypto.randomUUID()}` },
    });
    return response.data;
  });
};

// --- Reverse transaction ---

/**
 * Reverse a wallet transaction (admin only).
 * Supports reversing DEDUCT, REFUND, and TOP_UP transactions.
 * @param {Object} laravelAxios - Laravel axios instance (for getting JWT token)
 * @param {number} transactionId - Transaction ID to reverse
 * @param {string} reason - Reason for reversal (min 10 chars)
 * @returns {Promise<{rollback_transaction_id: number, balance_after: number, original_transaction_id: number, is_duplicate: boolean}>}
 */
WalletService.prototype.reverseTransaction = async function(laravelAxios, transactionId, reason) {
  validatePathParam(transactionId, 'integer');
  await this.getToken(laravelAxios);
  const idempotencyKey = `reverse-txn-${transactionId}-${crypto.randomUUID()}`;
  const walletAxios = this.getWalletAxios();
  const response = await walletAxios.post(`/api/v1/admin/wallets/transactions/${transactionId}/reverse`, {
    reason,
    idempotency_key: idempotencyKey,
  }, {
    headers: { 'Idempotency-Key': idempotencyKey },
  });
  return response.data;
};
